import {
  useState,
  useRef,
  type ComponentProps,
  type DragEvent,
  type ComponentPropsWithRef,
  type ReactNode,
} from 'react';
import { clsx } from 'clsx';
import s from './Dropzone.module.css';

export interface DropzoneProps extends ComponentPropsWithRef<'input'> {
  children?: ReactNode;
  className?: string;
  onFileDrop: (files: File[]) => void;
}

export const Dropzone = (props: DropzoneProps) => {
  const { children, className, onFileDrop, ...restProps } = props;
  const [isDragging, setIsDragging] = useState<boolean>(false);
  const inputRef = useRef<HTMLInputElement>(null);

  const handleEvent = (e: DragEvent) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDragEnter: ComponentProps<'div'>['onDragEnter'] = (e) => {
    handleEvent(e);
    setIsDragging(true);
  };
  const handleDragOver: ComponentProps<'div'>['onDragOver'] = (e) => {
    handleEvent(e);
    setIsDragging(true);
  };
  const handleDragLeave: ComponentProps<'div'>['onDragLeave'] = (e) => {
    handleEvent(e);
    setIsDragging(false);
  };

  const handleDrop: ComponentProps<'div'>['onDrop'] = (e) => {
    handleDragLeave(e);
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const newFiles = Array.from(e.dataTransfer.files);
      onFileDrop(newFiles);
    }
  };

  const handleClick: ComponentProps<'div'>['onClick'] = () => {
    if (inputRef.current) {
      inputRef.current.click();
    }
  };

  const handleChange: ComponentProps<'input'>['onChange'] = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      const newFiles = Array.from(e.target.files);
      onFileDrop(newFiles);
      e.target.value = '';
    }
  };

  const handleKeyDown: ComponentProps<'div'>['onKeyDown'] = (e) => {
    if (e.key === 'Enter' && inputRef.current) {
      inputRef.current.click();
    }
  };

  return (
    <div className={clsx(s.container, className)}>
      <div
        role="presentation"
        tabIndex={0}
        onClick={handleClick}
        onDragEnter={handleDragEnter}
        onDragOver={handleDragOver}
        onDragLeave={handleDragLeave}
        onKeyDown={handleKeyDown}
        onDrop={handleDrop}
        className={clsx({
          [s.dropzone]: true,
          [s.dragActive]: isDragging,
        })}
      >
        <input
          className={s.input}
          ref={inputRef}
          type="file"
          onChange={handleChange}
          {...restProps}
        />
        {children}
      </div>
    </div>
  );
};
